import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { MessageCircle, X, Send, UserRound, CalendarPlus, CheckCircle2 } from "lucide-react";
import { API, api, errMsg } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const KEY = (slug) => `hbl_chat_${slug}`;
const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const DAYS = Array.from({ length: 14 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() + i); return d; });
const sel_ = "h-9 w-full rounded-md border bg-background px-2 text-sm";

function BookingCard({ slug, ensure, initial }) {
  const { lang } = useT();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({ location_id: "", service_id: "", date: iso(new Date()) });
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState("");
  const [d, setD] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/public/c/${slug}/clinic`).then((r) => {
      const x = r.data;
      setData(x);
      const loc = x.locations.find((l) => l.id === initial?.location_id) || (x.locations.length === 1 ? x.locations[0] : null);
      const svc = loc && x.services.find((s) => s.id === initial?.service_id && s.prices?.[loc.id]);
      setSel((s) => ({ ...s, location_id: loc?.id || "", service_id: svc?.id || "" }));
    });
  }, [slug, initial]);
  useEffect(() => {
    setTime(""); setSlots(null);
    if (!sel.location_id || !sel.service_id) return;
    api.get(`/public/c/${slug}/slots`, { params: { ...sel, professional_id: "any" } }).then((r) => setSlots(r.data)).catch(() => setSlots([]));
  }, [slug, sel]);
  if (!data) return null;
  const svcs = data.services.filter((s) => s.prices?.[sel.location_id]);
  const book = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/public/c/${slug}/book`, { ...sel, professional_id: "any", time, ...d, language: lang, origin_url: window.location.origin });
      const s = await ensure();
      api.post(`/public/c/${slug}/chat/${s.id}/booked`, { token: s.token, manage_token: r.manage_token }).catch(() => {});
      setDone(r);
    } catch (e) { alert(errMsg(e)); } finally { setBusy(false); }
  };
  if (done) return (
    <div className="rounded-xl border bg-background p-3 text-sm space-y-2" data-testid="chat-booking-done">
      <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4 text-primary" />{done.checkout_url ? "Almost done – pay the deposit to confirm" : "Your appointment is booked!"}</div>
      <div className="text-xs text-muted-foreground">{svcs.find((s) => s.id === sel.service_id)?.name} · {sel.date} {time}</div>
      {done.checkout_url ? <a href={done.checkout_url} target="_blank" rel="noreferrer" data-testid="chat-booking-pay"><Button size="sm" className="rounded-full w-full">Pay deposit</Button></a>
        : <a href={`/manage/${done.manage_token}`} target="_blank" rel="noreferrer" className="text-xs text-primary font-semibold" data-testid="chat-booking-manage">View or manage booking</a>}
    </div>
  );
  return (
    <div className="rounded-xl border bg-background p-3 space-y-2.5 text-sm" data-testid="chat-booking-card">
      <div className="flex items-center gap-2 font-semibold"><CalendarPlus className="h-4 w-4 text-primary" />Book an appointment</div>
      {data.locations.length > 1 && <select className={sel_} data-testid="chat-book-location" value={sel.location_id} onChange={(e) => setSel({ ...sel, location_id: e.target.value, service_id: "" })}>
        <option value="">Choose a location</option>{data.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>}
      {sel.location_id && <select className={sel_} data-testid="chat-book-service" value={sel.service_id} onChange={(e) => setSel({ ...sel, service_id: e.target.value })}>
        <option value="">Choose a treatment</option>{svcs.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.prices[sel.location_id].price}</option>)}</select>}
      {sel.service_id && <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="chat-book-days">{DAYS.map((x) => { const v = iso(x); return (
        <button key={v} data-testid={`chat-book-day-${v}`} onClick={() => setSel({ ...sel, date: v })} className={`shrink-0 rounded-lg border px-2 py-1 text-center text-[11px] leading-tight ${sel.date === v ? "bg-primary text-primary-foreground border-primary" : ""}`}>
          {x.toLocaleDateString(lang, { weekday: "short" })}<br /><b>{x.getDate()}</b></button>); })}</div>}
      {slots && (slots.length ? <div className="flex flex-wrap gap-1.5" data-testid="chat-book-slots">{slots.map((s) => (
        <button key={s.time} data-testid={`chat-book-slot-${s.time}`} onClick={() => setTime(s.time)} className={`rounded-full border px-2.5 py-1 text-xs ${time === s.time ? "bg-primary text-primary-foreground border-primary" : ""}`}>{s.time}</button>))}</div>
        : <p className="text-xs text-muted-foreground" data-testid="chat-book-noslots">No free times this day – try another day.</p>)}
      {time && <div className="space-y-1.5">
        <div className="grid grid-cols-2 gap-1.5"><Input data-testid="chat-book-first" placeholder="First name" value={d.first_name} onChange={(e) => setD({ ...d, first_name: e.target.value })} />
          <Input data-testid="chat-book-last" placeholder="Last name" value={d.last_name} onChange={(e) => setD({ ...d, last_name: e.target.value })} /></div>
        <Input data-testid="chat-book-email" type="email" placeholder="Email" value={d.email} onChange={(e) => setD({ ...d, email: e.target.value })} />
        <Input data-testid="chat-book-phone" type="tel" placeholder="Phone" value={d.phone} onChange={(e) => setD({ ...d, phone: e.target.value })} />
        <Button size="sm" className="rounded-full w-full" data-testid="chat-book-confirm" disabled={busy || !d.first_name || !d.email.includes("@") || d.phone.length < 6} onClick={book}>{busy ? "Booking…" : `Book ${sel.date} at ${time}`}</Button>
      </div>}
    </div>
  );
}

function Bubble({ m }) {
  if (m.role === "system") return <p className="text-center text-[11px] text-muted-foreground py-1">{m.text}</p>;
  const mine = m.role === "visitor";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted rounded-bl-sm"}`}>
        {!mine && m.author && <div className="text-[10px] font-semibold opacity-60 mb-0.5">{m.author}</div>}
        {m.text}
      </div>
    </div>
  );
}

async function readStream(res, onText) {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "", status = null, book = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const d = JSON.parse(p.slice(6));
      if (d.t) onText(d.t);
      if (d.done) { status = d.status; book = d.book; }
    }
  }
  return { status, book };
}

export function ChatPanel({ slug, onClose, full }) {
  const [cfg, setCfg] = useState(null);
  const [sess, setSess] = useState(() => JSON.parse(localStorage.getItem(KEY(slug)) || "null"));
  const [msgs, setMsgs] = useState([]);
  const [status, setStatus] = useState("ai");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState({ name: "", contact: "", sent: false });
  const [localBook, setLocalBook] = useState(null);
  const end = useRef(null);

  useEffect(() => { api.get(`/public/c/${slug}/chat-config`).then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })); }, [slug]);
  const refresh = useCallback(() => {
    if (!sess) return;
    api.get(`/public/c/${slug}/chat/${sess.id}`, { params: { token: sess.token } })
      .then((r) => { setMsgs(r.data.messages); setStatus(r.data.status); })
      .catch(() => { localStorage.removeItem(KEY(slug)); setSess(null); setMsgs([]); });
  }, [slug, sess]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!sess || busy || status === "ai" || status === "closed") return undefined;
    const h = setInterval(refresh, 4000);
    return () => clearInterval(h);
  }, [sess, busy, status, refresh]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ensure = async () => {
    if (sess) return sess;
    const { data } = await api.post(`/public/c/${slug}/chat`, { page: window.location.pathname });
    localStorage.setItem(KEY(slug), JSON.stringify(data));
    setSess(data);
    return data;
  };
  const send = async (body = text, extra = {}) => {
    if (!body.trim() || busy) return;
    setBusy(true); setText("");
    try {
      const s = await ensure();
      setMsgs((m) => [...m, { id: Date.now(), role: "visitor", text: body }, { id: "live", role: "ai", author: cfg.assistant_name, text: "" }]);
      const res = await fetch(`${API}/public/c/${slug}/chat/${s.id}/message`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: s.token, text: body, ...extra }) });
      if (!res.ok) throw new Error((await res.json()).detail);
      const out = await readStream(res, (t) => setMsgs((m) => m.map((x) => (x.id === "live" ? { ...x, text: x.text + t } : x))));
      if (out.status) setStatus(out.status);
      if (out.book) setLocalBook(null);
    } catch (e) {
      setMsgs((m) => [...m.filter((x) => x.id !== "live"), { id: Date.now(), role: "system", text: e.message || "Message failed" }]);
    } finally {
      setBusy(false);
      setTimeout(refresh, 300);
    }
  };
  const handoff = async () => { const s = await ensure(); const r = await api.post(`/public/c/${slug}/chat/${s.id}/handoff`, { token: s.token }); setStatus(r.data.status); refresh(); };
  const restart = () => { localStorage.removeItem(KEY(slug)); setSess(null); setMsgs([]); setStatus("ai"); };

  if (!cfg || !cfg.enabled) return full ? <p className="p-6 text-sm text-muted-foreground">Chat is not available.</p> : null;
  const op = [...msgs].reverse().find((m) => m.role === "operator");
  const lastBook = [...msgs].reverse().find((m) => m.book);
  return (
    <div data-testid="chat-panel" className={`flex flex-col bg-card ${full ? "h-screen" : "h-[min(600px,80vh)] w-[min(380px,calc(100vw-32px))] rounded-2xl border shadow-2xl overflow-hidden"}`}>
      <div className="sidebar flex items-center justify-between px-4 py-3">
        <div><div className="font-sans font-bold text-sm">{cfg.assistant_name}</div>
          <div className="text-[11px] opacity-70" data-testid="chat-status">{status === "operator" ? `You're chatting with ${op?.author || "our team"}` : status === "waiting" ? "Connecting you with our team…" : status === "closed" ? "Chat closed" : "AI assistant · replies instantly"}</div></div>
        {onClose && <button onClick={onClose} data-testid="chat-close-btn" aria-label="Close"><X className="h-5 w-5" /></button>}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2.5" data-testid="chat-messages">
        <Bubble m={{ role: "ai", author: cfg.assistant_name, text: cfg.greeting }} />
        {msgs.map((m) => <div key={m.id} className="space-y-2"><Bubble m={m} />{m === lastBook && !localBook && <BookingCard slug={slug} ensure={ensure} initial={m.book} />}</div>)}
        {localBook && <BookingCard slug={slug} ensure={ensure} initial={localBook} />}
        {status === "waiting" && !contact.sent && (
          <div className="rounded-xl border p-3 space-y-2 bg-background" data-testid="chat-contact-form">
            <p className="text-xs text-muted-foreground">Leave your name and phone or email so we can reply even if you close this page.</p>
            <Input data-testid="chat-contact-name" placeholder="Name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
            <Input data-testid="chat-contact-info" placeholder="Phone or email" value={contact.contact} onChange={(e) => setContact({ ...contact, contact: e.target.value })} />
            <Button size="sm" className="rounded-full" data-testid="chat-contact-send" disabled={!contact.name || !contact.contact}
              onClick={() => { send(`My name is ${contact.name}, contact: ${contact.contact}`, { name: contact.name, contact: contact.contact }); setContact({ ...contact, sent: true }); }}>Send</Button>
          </div>
        )}
        <div ref={end} />
      </div>
      {status === "closed" ? (
        <div className="p-3 border-t"><Button className="w-full rounded-full" data-testid="chat-restart-btn" onClick={restart}>Start a new chat</Button></div>
      ) : (
        <div className="border-t p-3 space-y-2">
          <div className="flex gap-4">
            <button data-testid="chat-book-btn" onClick={() => setLocalBook({})} className="flex items-center gap-1 text-xs text-primary font-semibold"><CalendarPlus className="h-3.5 w-3.5" />Book here</button>
            {status === "ai" && msgs.length > 0 && <button data-testid="chat-handoff-btn" onClick={handoff} className="flex items-center gap-1 text-xs text-primary font-semibold"><UserRound className="h-3.5 w-3.5" />Talk to a person</button>}
          </div>
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <Input data-testid="chat-input" placeholder="Type your message…" value={text} onChange={(e) => setText(e.target.value)} maxLength={1500} />
            <Button size="icon" className="rounded-full shrink-0" data-testid="chat-send-btn" disabled={busy || !text.trim()}><Send className="h-4 w-4" /></Button>
          </form>
        </div>
      )}
    </div>
  );
}

export function ChatBubble({ slug }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="fixed bottom-5 right-4 z-50 flex flex-col items-end gap-3">
      {open && <ChatPanel slug={slug} onClose={() => setOpen(false)} />}
      {!open && <button data-testid="chat-bubble-btn" onClick={() => setOpen(true)} aria-label="Chat with us"
        className="h-14 w-14 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-xl hover:scale-105 transition-transform"><MessageCircle className="h-6 w-6" /></button>}
    </div>
  );
}

export default function ChatPage() {
  const { slug } = useParams();
  return <ChatPanel slug={slug} full />;
}
